import React, { useContext, useEffect, useState, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";

import {
  getCustomer,
  getBasketData,
  refetchBasketData,
  refetchCustomer,
} from "../queries/global";
import {
  NotificationContext,
  handleError,
} from "../context/NotificationContext";

import config from "../config/config";
import * as QUERIES from "../queries/query-functions";

import PromoCodeInput from "../components/promo-code-input";
import PreviousIcon from "../svg/previous";
import ArrowIcon from "../svg/arrow";
import { useSearchParams } from "react-router-dom";
import LoadingIndicator from "../components/loading-indicator";

const dialog_data = config.OPTION_FIELDS.join_and_book_memberships;

const event_tags_and_memberships = config.OPTION_FIELDS.event_tags_and_memberships;
const event_memberships = event_tags_and_memberships.memberships || [];
const event_tags = event_tags_and_memberships.tags ? event_tags_and_memberships.tags.map(tag => tag.spektrix_tag).filter(Boolean) : [];


export default function CheckForPriority(props) {
  const [searchParams] = useSearchParams();
  const { notification, setNotification } = useContext(NotificationContext);
  const [type, setType] = useState('');
  

  const { data: customer } = getCustomer();
  const { data: basketData } = getBasketData();

  const [isInPriority, setIsInPriority] = useState(false);
  const dialogRef = useRef(null);
  const containerRef = useRef(null);

  const { data: eventInstancesData } = useQuery({
    queryKey: ["eventInstancesData", config.EVENT.event_id],
    queryFn: () => QUERIES.getEventInstances(config.EVENT.event_id),
    staleTime: 86400000,
    refetchOnWindowFocus: false,
  });

  const addMembership = useMutation({
    mutationFn: (membershipId) =>
      QUERIES.addMembership([{ membership: membershipId, autoRenew: true }]),
    onError: (error) => {
      handleError(error.message, setNotification);
    },
    onSuccess: () => {
      refetchBasketData();
    },
  });

  useEffect(() => {
    // Stop dialog being closed even by esc key
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleClose = (e) => {
      e.preventDefault();
      dialogRef.current.showModal();
    };

    dialog.addEventListener("cancel", handleClose);
    dialog.addEventListener("close", handleClose);

    return () => {
      dialog.removeEventListener("cancel", handleClose);
      dialog.removeEventListener("close", handleClose);
    };
  }, []);

  useEffect(() => {
    if (!eventInstancesData) return;

    const instance_data = eventInstancesData.find(
      (instance) => instance.id == config.INSTANCE_ID
    );
    // If no data, exit
    if (!instance_data) return;

    const isOnSale = instance_data.isOnSale;
    // If on sale, exit
    if (isOnSale) {
      props.setCheckedForPriority(true);
      return;
    }    

    // If not on sale and not logged and no promo code or valid membership in basket show dialog and exit
    if (
      !customer &&
      !basketData?.promoCode &&
      !basketData?.membershipSubscriptions?.length
    ) {
      setIsInPriority(true);
      return;
    }

    if (customer) {
      // Check if any customer memberships are valid
      const hasMatchingTag = customer.tags?.some(tag => event_tags.includes(tag.id));
      const hasMatchingMembership = customer.subscriptions?.some(membership => event_memberships.includes(membership.membership.id));

      if (hasMatchingTag || hasMatchingMembership) {        
      // if (customer.subscriptions?.length) {        
          props.setCheckedForPriority(true);
          return;
      }
    }

    if (basketData) {
      // Check if basket contains a valid membership
      if (basketData.membershipSubscriptions?.length) {   
          props.setCheckedForPriority(true);   
          return;
      }

      // Check if basket contains a valid promocode
      if (basketData.promoCode) {
        props.setCheckedForPriority(true);
        return;
      }
    }

    // If none of the access requirements are met show the dialog
    setIsInPriority(true);
  }, [eventInstancesData, customer, basketData]);

  useEffect(() => {
    if (!isInPriority) return;
    dialogRef.current?.showModal();
    const pathType =  searchParams.get('type');

    if(!props.checkedForPriority && pathType) {
      setType(pathType);
    }
  }, [isInPriority]);


  useEffect(()=>{
    containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  },[type])

  return (
    <div className="booking-pathway__priority-check">
      <dialog ref={dialogRef} className="priority-booking-dialog">
        <div className="dialog-container" ref={containerRef}>
          <div className="dialog-content">
            {type !== 'join' ?
              <>
                <div className="priority-booking-dialog__header">
                  <h1 className="priority-booking-dialog__title">Unlock access to {type !== 'closed-event' ? 'priority booking' : 'book now'}</h1>
                </div>
                <div className="priority-booking-dialog__content">
                  
                  <div className="priority-booking-dialog__content-col priority-booking-dialog__content-col--login">
                    {!customer &&
                      <LoginPriority/>
                    }
                    {customer && (!customer.tags?.some(tag => event_tags.includes(tag.id)) || !customer.subscriptions?.some(membership => event_memberships.includes(membership.membership.id))) &&                       
                        <p className="type-h5">
                          {
                            type !== 'closed-event' ?
                              'You don’t have access to priority booking for this event. Please try general sale or check your eligibility'
                              : 'You don’t have access to book now for this event. Please check your eligibility or contact the box office team for assistance.'
                          }
                        </p>                      
                    }
                  </div>
                  <div className="priority-booking-dialog__content-col">
                      <PromoCodeInput descriptionText='Have a promo code to access booking for this event? Enter it here' buttonText='Submit' />
                      {
                        type !== 'closed-event' &&                        
                        <div className="join-book-element">
                          <h2 className="type-h4">Not a member yet?</h2>
                          <button
                              type="button"
                              className="btn btn--pink-yellow"
                              onClick={() => setType('join')}
                            >
                              Join and book now
                            </button>
                        </div>
                      }
                  </div>

                </div>
                <div className="priority-booking-dialog__footer">                  
                    <a href={config.EVENT_LINK} className="btn btn--back btn--link"><ArrowIcon/> Back to event page</a>
                </div>
              </>
              : <>
                <div className="priority-booking-dialog__header">
                  <h1 className="priority-booking-dialog__title">Which membership tier suits you?</h1>

                  {dialog_data?.introduction && (
                    <div
                      className="priority-booking-dialog__introduction"
                      dangerouslySetInnerHTML={{ __html: dialog_data.introduction.innerHTML }}
                    ></div>
                  )}
                </div>
                <div className="priority-booking-dialog__memberships memberships">                
                    <div className="memberships__item">
                      <div className="memberships__item-header">
                        {dialog_data?.playhouse_pass.title && <h2 className="memberships__item-title">{dialog_data?.playhouse_pass.title.innerHTML}</h2>}
                        {dialog_data?.playhouse_pass.price && <p className="memberships__item-price">{dialog_data?.playhouse_pass.price.innerHTML}</p>}
                        {
                          dialog_data?.playhouse_pass.membership && 
                          <button
                          type="button"
                          className="btn btn--primary btn--yellow-white"
                          onClick={() => addMembership.mutate(dialog_data?.playhouse_pass.membership.innerHTML)}
                        >
                          Add to basket and continue
                        </button>
                        }
                      </div>
                      {dialog_data?.playhouse_pass.benefits && <details className="memberships__detail">
                        <summary className="memberships__detail-title">
                          Benefits:
                          <span
                            className="memberships__detail-icon btn btn--square"
                            aria-hidden="true"
                          ><PreviousIcon /></span>
                        </summary>
                        <div
                          className="memberships__detail-content"
                          dangerouslySetInnerHTML={{ __html: dialog_data?.playhouse_pass.benefits.innerHTML }}
                        ></div>
                      </details>
                      }
                    </div>

                    <div className="memberships__item memberships__item--premium">
                      <div className="memberships__item-header">
                        {dialog_data?.playhouse_pass_premium.title && <h2 className="memberships__item-title">{dialog_data?.playhouse_pass_premium.title.innerHTML}</h2>}
                        {dialog_data?.playhouse_pass_premium.price && <p className="memberships__item-price">{dialog_data?.playhouse_pass_premium.price.innerHTML}</p>}
                        {dialog_data?.playhouse_pass_premium.membership && <button
                          type="button"
                          className="btn btn--primary btn--yellow-white"
                          onClick={() => addMembership.mutate(dialog_data?.playhouse_pass_premium.membership.innerHTML)}
                        >
                          Add to basket and continue
                        </button>}
                      </div>
                      {dialog_data?.playhouse_pass_premium.benefits && <details className="memberships__detail">
                        <summary className="memberships__detail-title">
                          Benefits:
                          <span
                            className="memberships__detail-icon btn btn--square"
                            aria-hidden="true"
                          ><PreviousIcon /></span>
                        </summary>
                        <div
                          className="memberships__detail-content"
                          dangerouslySetInnerHTML={{ __html: dialog_data?.playhouse_pass_premium.benefits.innerHTML }}
                        ></div>
                      </details>
                      }
                    </div>
                </div>
                
                <div className="priority-booking-dialog__footer">
                  <a href={config.EVENT_LINK} className="btn btn--back">Wait for general on-sale instead</a>
                  <p className="priority-booking-dialog__footer-text">Already a member? 
                    <button className="btn--link" type="button" onClick={( )=> { setType('')}}>Login</button></p>
                </div>
              </>
              }
          </div>
        </div>
      </dialog>
    </div>
  );
}


const LoginPriority = ({}) => {

  const { notification, setNotification } = useContext(NotificationContext);
    const [email, setEmail] = useState(null);
    const [password, setPassword] = useState(null);
    const [emailAlert, setEmailAlert] = useState(null);
    const [passwordAlert, setPasswordAlert] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
  
    const emailInputRef = useRef(null);
  
    const {
      data: customer,
      isPending: customerPending,
      isFetching: customerFetching,
    } = getCustomer();
    // const [searchParams, setSearchParams] = useSearchParams();
  
    // const redirect_param = searchParams.get("r");
    // const from = redirect_param ? redirect_param : "/my-account";
  
    const logIn = useMutation({
      mutationFn: () => QUERIES.logIn({ email, password }),
      onError: (error) => {
        handleError(error.message, setNotification);
      },
      onSuccess: (data) => {
        console.log("refetchCustomer", data);
  
        refetchCustomer();
      },
    });
  
    const resetPassword = useMutation({
      mutationFn: () => QUERIES.resetPassword(email),
      onError: (error) => {
        handleError(error.message, setNotification);
      },
      onSuccess: () => {
        setSuccessMessage("A reset link has been sent to your email address.");
      },
    });
  
    const handleLogin = (e) => {
      e.preventDefault();
      let has_alert = false;
      if (!email || !emailInputRef.current.checkValidity()) {
        has_alert = true;
        setEmailAlert("Please provide a valid email address.");
      }
      if (!password) {
        has_alert = true;
        setPasswordAlert("Please provide a password.");
      }
      if (has_alert) return;
      logIn.mutateAsync();
    };
  
    const handleResetPassword = () => {
      if (!email || !emailInputRef.current.checkValidity()) {
        setEmailAlert("Please provide a valid email address.");
        return;
      }
      resetPassword.mutateAsync();
    };
  
    // useEffect(() => {
    //   if (!customer || logIn.isPending) return;
  
      // If the user is logged in redirect them
      // window.location = from;
    // }, [customer, logIn.isPending]);

    if (customerPending || customerFetching || customer)
        return <LoadingIndicator />;

  return (
    <>
      <h2 className="type-h4">Login</h2>
      <form className="booking-pathway__login--form login-form" onSubmit={handleLogin}>
        <div className="form-field form-field__email">
            <label htmlFor="spektrixLoginEmail">
                Email address:
                <span className="label__required">(required)</span>
            </label>
            <input
              type="email"
              ref={emailInputRef}
              id="spektrixLoginEmail"
              autoComplete="email"
              onChange={(e) => {
                setEmailAlert(null);
                setEmail(e.target.value);
              }}
              className={emailAlert ? "form-field--error" : ""}
              required
            />
          </div>

          <div className="form-field form-field__password">
            <label htmlFor="spektrixLoginPassword">
                Password:
                <span className="label__required">(required)</span>
            </label>
            <input
              type="password"
              id="spektrixLoginPassword"
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
              className={passwordAlert ? "form-field--error" : ""}
              required
            />
          </div>

          <div role="alert" className="alert-messages">
            {successMessage && (
              <p className="confirmation-message">{successMessage}</p>
            )}
            {emailAlert && <p className="error-message">{emailAlert}</p>}
            {passwordAlert && <p className="error-message">{passwordAlert}</p>}
          </div>

          <div className="login-form__footer">
            
            <button
              type="submit"
              className="btn btn--primary"
              // onClick={() => handleLogin()}
            >
              Login
            </button>
          </div>

          <p className="login-form__forgot-password">
            <button
              type="button"
              className="login-form__forgot-password--link btn--link"
              onClick={() => handleResetPassword()}
            >
              Forgot password?
            </button>
          </p>
        </form>    
    </>

  )
}